import cogoToast from '@successtar/cogo-toast'
import { useSessionStateStore, useSessionReset } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import MidtransUtils from '@renderer/utils/midtransUtils'
import { useInterval } from '@renderer/utils/useInterval'
import Spinner from '@renderer/components/Spinner'
import QrCodeImage from '@renderer/components/QrCodeImage'
import { useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import { FaUndo, FaCreditCard } from 'react-icons/fa'
import { Package } from '@renderer/types/configState'
import { useTranslation } from 'react-i18next'
import { ActionType, ActivityType, TransactionStatus } from '@renderer/types/sessionState'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'
import { getNextPageAfterPaymentFlow } from '@renderer/utils/navigationUtils'
import { qrActivityLogger } from '@renderer/utils/qrLoadReporter'

const PaymentPage: React.FC = () => {
  const { t } = useTranslation()
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const config = globalConfig?.pages.payment
  const welcomeSettings = getPageSettings('welcome')
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  const isMounted = useRef(true)

  // Helper to safely update state/logs only when component is mounted
  const safeUpdate = (callback: () => void): void => {
    if (isMounted.current) {
      callback()
    }
  }

  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [snapToken, setSnapToken] = useState<string | null>(null)
  const [clientKey, setClientKey] = useState<string | null>(null)
  const [paymentType, setPaymentType] = useState<'core' | 'snap'>('core')
  const [isExpired, setIsExpired] = useState(false)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [showSkipGuide, setShowSkipGuide] = useState(false)
  const [snapPaymentOpened, setSnapPaymentOpened] = useState(false)
  const {
    id,
    transaction,
    setSessionState,
    userPreferences,
    mode,
    uploadSessionState,
    logActivity
  } = useSessionStateStore()
  const sessionReset = useSessionReset()
  const navigate = useNavigate()
  const { projectId } = useParams()

  // Initialize payment countdown
  const paymentCountdown = useCountdown(0) // We'll set the actual seconds when payment is initiated

  // Initialize auto-reset countdown (15 seconds)
  const autoResetCountdown = useCountdown(15)

  // Set up auto-reset countdown callbacks
  useEffect(() => {
    autoResetCountdown.setOnTick(() => {
      setRemainingTime(autoResetCountdown.getFormattedRemainingSeconds())
    })

    autoResetCountdown.setOnComplete(() => {
      sessionReset()
    })
  }, [])

  // Watch for payment expiration to start auto-reset countdown
  useEffect(() => {
    if (isExpired) {
      autoResetCountdown.start()
      return (): void => {
        autoResetCountdown.stop()
      }
    }
    return
  }, [isExpired])

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      const remainingMs = expiryTimestamp - serverTimestamp
      return Math.max(0, Math.floor(remainingMs / 1000))
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return 0
    }
  }

  const handleCountdownEnd = async (): Promise<void> => {
    if (!isMounted.current) {
      return
    }
    await checkPaymentStatus()
    safeUpdate(() => {
      setIsCheckingPayment(false)
      setRemainingTime('00:00')
      setIsExpired(true)
      setSessionState({
        transaction: {
          status: 'expire'
        },
        timeline: {
          activityLogs: [
            {
              page: 'payment',
              type: 'payment',
              action: 'timeout',
              details: {
                orderId: transaction.orderId,
                paymentType
              },
              timestamp: new Date()
            }
          ]
        }
      })
    })
  }

  // Set up payment countdown callbacks
  useEffect(() => {
    paymentCountdown.setOnTick(() => {
      setRemainingTime(paymentCountdown.getFormattedRemainingSeconds())
    })

    paymentCountdown.setOnComplete(handleCountdownEnd)
  }, [])

  const getDefaultPrice = (): number => {
    const packages = globalConfig?.pages.checkout?.settings?.packages || []
    let selectedPackages: Package[] = []

    if (mode === 'capture') {
      selectedPackages = [
        packages.find((pkg) => pkg.type === 'print') || null,
        packages.find((pkg) => pkg.type === 'gif') || null,
        packages.find((pkg) => pkg.type === 'recording') || null
      ].filter((pkg) => pkg !== null)
    } else if (mode === 'record') {
      selectedPackages = [packages.find((pkg) => pkg.type === 'video') || null].filter(
        (pkg) => pkg !== null
      )
    }

    return selectedPackages.reduce((subtotal, pkg) => subtotal + (pkg.price || 0), 0)
  }

  const cleanupPayment = useCallback(async (): Promise<void> => {
    if (transaction.orderId && transaction.status !== 'settlement') {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${transaction.orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }, [transaction.orderId, transaction.status])

  useEffect(() => {
    const defaultPrice = getDefaultPrice()
    if (transaction.amount === 0 || !transaction.amount) {
      setSessionState({
        transaction: {
          amount: defaultPrice
        }
      })
    }
    // Upload session state on mount
    uploadSessionState()
    return (): void => {
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      autoResetCountdown.stop()
      cleanupPayment()
    }
  }, [])

  // Handle orderId changes
  useEffect(() => {
    // When component unmounts or orderId changes, cleanup previous payment if needed
    return (): void => {
      if (transaction.orderId && transaction.status !== 'settlement') {
        cleanupPayment()
      }
    }
  }, [transaction.orderId, cleanupPayment])

  // useEffect(() => {
  //   // You can also change below url value to any script url you wish to load,
  //   const midtransScriptUrl = 'https://app.midtrans.com/snap/snap.js'

  //   const scriptTag = document.createElement('script')
  //   scriptTag.src = midtransScriptUrl

  //   document.body.appendChild(scriptTag)

  //   return (): void => {
  //     document.body.removeChild(scriptTag)
  //   }
  // }, [])

  const handleSnapPaymentSuccess = (result: unknown): void => {
    console.log('Snap payment successful:', result)
    setIsCheckingPayment(false)
    cogoToast.success(t('payment.successMessage', 'Payment successful!'))
    setSessionState({
      transaction: {
        status: 'settlement'
      },
      timeline: {
        activityLogs: [
          {
            page: 'payment',
            type: 'payment',
            action: 'success',
            details: {
              orderId: transaction.orderId,
              paymentType: 'snap',
              snapResult: result
            },
            timestamp: new Date()
          }
        ]
      }
    })
    uploadSessionState()
    const nextPage = getNextPageAfterPaymentFlow(globalConfig, {
      startWithPayment: welcomeSettings?.startWithPayment || false
    })
    navigate(`/${projectId}/${nextPage}`)
  }

  const handleSnapPaymentError = (result: unknown): void => {
    console.error('Snap payment error:', result)
    cogoToast.error(t('payment.snapError', 'Payment failed. Please try again.'))
    setSessionState({
      timeline: {
        activityLogs: [
          {
            page: 'payment',
            type: 'payment',
            action: 'failure',
            details: {
              orderId: transaction.orderId,
              paymentType: 'snap',
              error: result
            },
            timestamp: new Date()
          }
        ]
      }
    })
    navigate(`/${projectId}`)
  }

  const handleSnapPaymentClose = (): void => {
    setSnapPaymentOpened(false)
    // Continue checking payment status in case user completed payment
    setIsCheckingPayment(true)
  }

  const openSnapPayment = async (): Promise<void> => {
    if (!snapToken || !clientKey || snapPaymentOpened) return

    try {
      window.snap.pay(snapToken, {
        onSuccess: (result: unknown) => {
          console.log('Snap payment success:', result)
          handleSnapPaymentSuccess?.(result)
        },
        onError: (result: unknown) => {
          console.error('Snap payment error:', result)
          handleSnapPaymentError?.(result)
        },
        onClose: () => {
          console.log('Snap payment popup closed')
          handleSnapPaymentClose?.()
        }
      })
    } catch (error) {
      console.error('Error opening Snap payment:', error)
      throw error
    }
  }

  const initiatePayment = async (): Promise<void> => {
    try {
      const data = await MidtransUtils.initiatePayment(
        transaction.amount || getDefaultPrice(),
        id || '',
        config?.settings.paymentTimeout || 120,
        projectId || '',
        userPreferences.filterId || '',
        userPreferences.frameId || '',
        transaction.discount || 0
      )

      if (data.order_id) {
        const safePaymentType = data.payment_type === 'snap' ? 'snap' : 'core'
        setPaymentType(safePaymentType)

        // Calculate initial remaining time
        const initialRemainingSeconds = calculateRemainingTime(data.expiry_time, data.server_time)

        if (initialRemainingSeconds <= 0) {
          setIsExpired(true)
          return
        }

        const sessionData = {
          transaction: {
            orderId: data.order_id,
            qrUrl: '',
            expiryTime: data.expiry_time,
            serverTime: data.server_time,
            status: 'pending' as TransactionStatus,
            amount: transaction.amount,
            discount: transaction.discount || 0,
            paymentType: data.payment_type
          },
          timeline: {
            activityLogs: [
              {
                page: 'payment',
                type: 'payment' as ActivityType,
                action: 'start' as ActionType,
                details: {
                  amount: transaction.amount,
                  orderId: data.order_id,
                  expiryTime: data.expiry_time,
                  serverTime: data.server_time,
                  remainingSeconds: initialRemainingSeconds,
                  paymentType: data.payment_type
                },
                timestamp: new Date(data.server_time)
              }
            ]
          }
        }

        if (data.payment_type === 'core' && data.qr_url) {
          // Core API with QR code
          const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')
          setQrUrl(sanitizedQrUrl)
          sessionData.transaction.qrUrl = sanitizedQrUrl
        } else if (data.payment_type === 'snap' && data.snap_token) {
          // Snap payment
          setSnapToken(data.snap_token)
          setClientKey(data.client_key || '')
        }

        setSessionState(sessionData)
        setIsExpired(false)

        // Stop any existing countdown and start new one
        paymentCountdown.stop()
        paymentCountdown.setRemainingSeconds(initialRemainingSeconds)
        paymentCountdown.start()

        setIsCheckingPayment(true)
        isMounted.current = true
      }
    } catch (e: unknown) {
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'payment',
              type: 'payment',
              action: 'failure',
              details: {
                error: (e as Error).message
              },
              timestamp: new Date()
            }
          ]
        }
      })
      console.error('Error initiating payment:', e)
      cogoToast.error(<span>{(e as Error).message}</span>)
    }
  }

  useEffect(() => {
    initiatePayment()
    return (): void => {
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      autoResetCountdown.stop()
    }
  }, [])

  useInterval(
    async () => {
      await checkPaymentStatus()
    },
    isCheckingPayment ? 5000 : null
  )

  const checkPaymentStatus = async (isButtonClick: boolean = false): Promise<void> => {
    if (!transaction.expiryTime || !transaction.serverTime || !isMounted.current) {
      return
    }

    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(
        transaction.orderId || 'order-default-id'
      )

      // If payment has expired, update the UI
      if (paymentData.isExpired) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setIsExpired(true)
          setSessionState({
            transaction: {
              status: 'expire'
            },
            timeline: {
              activityLogs: [
                {
                  page: 'payment',
                  type: 'payment',
                  action: 'timeout',
                  details: {
                    orderId: transaction.orderId,
                    expiryTime: transaction.expiryTime,
                    serverTime: paymentData.serverTime,
                    transactionStatus: paymentData.transactionStatus,
                    paymentType
                  },
                  timestamp: new Date(paymentData.serverTime)
                }
              ]
            }
          })
          uploadSessionState()
        })
        return
      }

      if (paymentData.paid) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          cogoToast.success(t('payment.successMessage', 'Payment successful!'))
          setSessionState({
            transaction: {
              status: 'settlement'
            },
            timeline: {
              activityLogs: [
                {
                  page: 'payment',
                  type: 'payment',
                  action: 'success',
                  details: {
                    orderId: transaction.orderId,
                    serverTime: paymentData.serverTime,
                    transactionStatus: paymentData.transactionStatus,
                    paymentType
                  },
                  timestamp: new Date(paymentData.serverTime)
                }
              ]
            }
          })
          uploadSessionState()
          const nextPage = getNextPageAfterPaymentFlow(globalConfig, {
            startWithPayment: welcomeSettings?.startWithPayment || false
          })
          navigate(`/${projectId}/${nextPage}`)
        })
      } else if (isButtonClick) {
        safeUpdate(() => {
          cogoToast.error(t('payment.notSuccessful', 'Payment not successful. Please try again.'))
        })
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
      if (isButtonClick) {
        safeUpdate(() => {
          cogoToast.error((e as Error).message)
        })
      }
    }
  }

  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('hasSeenSkipPaymentGuide')
    if (!hasSeenGuide) {
      setShowSkipGuide(true)
      localStorage.setItem('hasSeenSkipPaymentGuide', 'true')

      // Auto-hide the tooltip after 10 seconds
      const timer = setTimeout(() => {
        setShowSkipGuide(false)
      }, 10000)

      return (): void => clearTimeout(timer)
    }
    return
  }, [])

  const renderPaymentInterface = (): JSX.Element => {
    if (isExpired) {
      return (
        <div
          className="flex flex-col items-center justify-center cursor-pointer transition-all duration-300 hover:scale-105"
          onClick={(): void => {
            // When manually clicking reset, stop the auto-reset countdown
            autoResetCountdown.stop()
            sessionReset()
          }}
        >
          <div className="bg-gray-100 p-6 rounded-full">
            <FaUndo className="w-16 h-16 text-gray-600" />
          </div>
          <div className="text-center">
            <p className="text-xl font-medium text-gray-700 mb-2">
              {t('payment.expired', 'Payment code expired')}
            </p>
            <p className="text-sm text-gray-500">
              {t('payment.restartPrompt', 'Click to restart transaction')}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {t('payment.autoRestart', 'Auto-restart in')} {remainingTime}
            </p>
          </div>
          <div className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-full text-sm font-medium hover:bg-blue-600 transition-colors duration-200">
            {t('payment.restart', 'Restart Transaction')}
          </div>
        </div>
      )
    }

    if (paymentType === 'core' && qrUrl) {
      return (
        <QrCodeImage
          qrUrl={qrUrl}
          className="w-full h-full object-contain rounded-lg"
          onEvent={qrActivityLogger(logActivity, 'photophoria-payment')}
        />
      )
    }

    if (paymentType === 'snap' && snapToken) {
      return (
        <div className="flex flex-col items-center justify-center gap-6">
          <div className="bg-blue-100 p-6 rounded-full">
            <FaCreditCard className="w-16 h-16 text-blue-600" />
          </div>
          <div className="text-center">
            <p className="text-xl font-medium text-gray-700 mb-4">
              {t('payment.snapReady', 'Payment gateway ready')}
            </p>
            <button
              onClick={openSnapPayment}
              disabled={snapPaymentOpened}
              className="px-8 py-3 bg-blue-500 text-white rounded-full text-lg font-medium hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {snapPaymentOpened
                ? t('payment.snapOpening', 'Opening payment...')
                : t('payment.openSnap', 'Open Payment')}
            </button>
          </div>
          <p className="text-sm text-gray-500 text-center max-w-md">
            {t(
              'payment.snapInstruction',
              'Click the button above to open the payment window and complete your transaction.'
            )}
          </p>
        </div>
      )
    }

    return <Spinner />
  }

  // Call all hooks at the top level (before any early returns)
  const cachedPaymentBg = useCachedBackgroundImage('/assets/photophoria/photophoria-payment.webp')

  return (
    <div
      className="w-full h-full flex items-center justify-center p-4 bg-whiteout bg-cover bg-center"
      data-page="payment"
      style={{
        backgroundImage: `url(${cachedPaymentBg})`
      }}
    >
      {showSkipGuide && (
        <div className="absolute top-16 right-12 z-50 bg-gray-200 p-4 rounded-lg shadow-lg max-w-xs">
          <div className="relative">
            <div className="absolute -top-6 right-4 w-4 h-4 bg-gray-200 transform rotate-45" />
            <p className="text-sm text-gray-700">
              {t(
                'payment.skipGuide',
                'Need to skip payment? Click the money icon in the top right corner and enter the PIN.'
              )}
            </p>
            <button
              onClick={(): void => setShowSkipGuide(false)}
              className="absolute -top-2 -right-2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <h1 className="absolute top-[260px] font-bold text-5xl">
        {formatAmount(transaction.amount || getDefaultPrice(), projectCurrency)}
      </h1>
      <div className="absolute bottom-[180px] w-[400px] h-[400px] flex justify-center items-center mt-48">
        {renderPaymentInterface()}
      </div>
      <p className="absolute right-20 bottom-20 text-3xl font-bold text-black">
        {remainingTime || '...'}
      </p>
    </div>
  )
}

export default PaymentPage
